import React, { useState } from 'react';

const ContactForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (replace with actual form handler)
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
        onClose();
      }, 2000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-60 transition-opacity duration-500"
        style={{ backdropFilter: 'blur(10px)' }}
        onClick={onClose}
      />
      
      {/* Contact Form Panel */}
      <div 
        className={`relative bg-black border border-white border-opacity-20 rounded-lg p-8 max-w-md w-full mx-4 transform transition-all duration-500 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.9) 100%)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)'
        }}
      >
        {/* Fluorescent Border Lines */}
        <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
          {/* Top line */}
          <div 
            className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-60"
            style={{
              boxShadow: '0 0 20px rgba(255,255,255,0.5)',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          />
          {/* Bottom line */}
          <div 
            className="absolute bottom-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-60"
            style={{
              boxShadow: '0 0 20px rgba(255,255,255,0.5)',
              animation: 'pulse 2s ease-in-out infinite reverse'
            }}
          />
          {/* Left line */}
          <div 
            className="absolute left-0 top-1/4 h-1/2 w-px bg-gradient-to-b from-transparent via-white to-transparent opacity-40"
            style={{
              boxShadow: '0 0 15px rgba(255,255,255,0.3)',
              animation: 'pulse 3s ease-in-out infinite'
            }}
          />
          {/* Right line */}
          <div 
            className="absolute right-0 top-1/4 h-1/2 w-px bg-gradient-to-b from-transparent via-white to-transparent opacity-40"
            style={{
              boxShadow: '0 0 15px rgba(255,255,255,0.3)',
              animation: 'pulse 3s ease-in-out infinite reverse'
            }}
          />
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white text-xl hover:text-gray-300 transition-colors z-10"
        >
          ✕
        </button>

        {/* Form Content */}
        <div className="relative z-10">
          {!isSubmitted ? (
            <>
              <h2 className="text-2xl font-thin text-white mb-6 tracking-wider">
                CONTACT
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white border-opacity-30 text-white placeholder-gray-400 py-2 px-0 focus:outline-none focus:border-opacity-80 transition-all duration-300"
                    placeholder="Name"
                    style={{ fontWeight: '100' }}
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 focus-within:w-full" />
                </div>

                {/* Email Field */}
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white border-opacity-30 text-white placeholder-gray-400 py-2 px-0 focus:outline-none focus:border-opacity-80 transition-all duration-300"
                    placeholder="Email"
                    style={{ fontWeight: '100' }}
                  />
                </div>

                {/* Subject Field */}
                <div className="relative">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-white border-opacity-30 text-white placeholder-gray-400 py-2 px-0 focus:outline-none focus:border-opacity-80 transition-all duration-300"
                    placeholder="Subject"
                    style={{ fontWeight: '100' }}
                  />
                </div>

                {/* Message Field */}
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full bg-transparent border border-white border-opacity-30 text-white placeholder-gray-400 p-3 focus:outline-none focus:border-opacity-80 transition-all duration-300 resize-none rounded"
                    placeholder="Message"
                    style={{ fontWeight: '100' }}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full mt-6 py-3 px-6 border border-white border-opacity-30 text-white font-thin tracking-wider transition-all duration-300 ${
                    isSubmitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-white hover:text-black hover:border-opacity-100'
                  }`}
                  style={{
                    background: isSubmitting ? 'rgba(255,255,255,0.1)' : 'transparent'
                  }}
                >
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                </button>
              </form>
            </>
          ) : (
            /* Success Message */
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✓</div>
              <h3 className="text-xl font-thin text-white mb-2 tracking-wider">
                MESSAGE SENT
              </h3>
              <p className="text-gray-400 font-thin">
                Thank you for reaching out. I'll get back to you soon.
              </p>
            </div>
          )}
        </div>

        {/* CSS Animations */}
        <style jsx>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.8; }
          }
          
          input:focus + div,
          textarea:focus + div {
            width: 100%;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ContactForm;
